import { useState, useEffect, useCallback, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowsClockwise,
  Plus,
  Warning,
  CheckCircle,
  PencilSimple,
  ClockCounterClockwise,
  Buildings,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { networkLabel } from "@/lib/networkLabels";

// Inline network pill — same palette as the AdminBrands hub so admins
// instantly recognise feed partners across screens. Kept local to avoid
// a new shared component for this single use. Labels come from
// @/lib/networkLabels (single source); only the chip color stays local.
const NETWORK_PILL_COLORS = {
  impact: "bg-slate-100 text-slate-700",
  partnerize: "bg-indigo-50 text-indigo-700",
  skimlinks: "bg-orange-50 text-orange-700",
  mrge: "bg-emerald-50 text-emerald-700",
  cj: "bg-amber-50 text-amber-700",
  pubadhub: "bg-cyan-50 text-cyan-700",
  pubadhub_kevin: "bg-teal-50 text-teal-700",
  admedia: "bg-rose-50 text-rose-700",
  flexoffers: "bg-violet-50 text-violet-700",
  shopnomix_kevin: "bg-pink-50 text-pink-700",
  siteplug: "bg-sky-50 text-sky-700",
  maxbounty: "bg-cyan-50 text-cyan-700",
  cashbackhive: "bg-teal-50 text-teal-700",
  fatcoupon: "bg-rose-50 text-rose-700",
  giddyup: "bg-teal-50 text-teal-700",
  rga_vishy: "bg-red-50 text-red-700",
  rga_ashok: "bg-pink-50 text-pink-700",
};
const NetworkPill = ({ network }) => {
  const n = (network || "impact").toLowerCase();
  const cls = NETWORK_PILL_COLORS[n] || "bg-slate-100 text-slate-700";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${cls}`}
      data-testid={`brand-sync-network-pill-${n}`}
    >
      {networkLabel(n)}
    </span>
  );
};

// Admin Impact reconciliation dashboard.
// Three tabs: New (pending review) · At risk · Sync runs.
export default function AdminBrandsSync() {
  const [pending, setPending] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingTruncated, setPendingTruncated] = useState(false);
  const didFetch = useRef(false);

  const pendingCountLabel = pendingTruncated && pendingTotal >= 500
    ? `${pendingTotal}+`
    : String(pendingTotal || pending.length);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, r] = await Promise.all([
        API.get("/admin/brands/pending-review"),
        API.get("/admin/brands/at-risk"),
        API.get("/admin/brands/reconcile/runs?limit=20"),
      ]);
      const brands = p.data?.brands || [];
      setPending(brands);
      setPendingTotal(typeof p.data?.total === "number" ? p.data.total : brands.length);
      setPendingTruncated(Boolean(p.data?.truncated));
      setAtRisk(a.data?.brands || []);
      setRuns(r.data?.runs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load sync data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetchAll();
  }, [fetchAll]);

  const runNow = async () => {
    if (running) return;
    if (!window.confirm("Run a full Impact reconciliation now? Takes ~1-2 min.")) return;
    setRunning(true);
    const t = toast.loading("Reconciling against Impact…");
    try {
      const { data } = await API.post("/admin/brands/reconcile");
      toast.dismiss(t);
      toast.success(
        `Done · ${data.added_count || 0} new · ${data.newly_at_risk_count || 0} at-risk · ${data.recovered_count || 0} recovered`
      );
      fetchAll();
    } catch (err) {
      toast.dismiss(t);
      toast.error(err.response?.data?.detail || "Reconciliation failed");
    } finally {
      setRunning(false);
    }
  };

  const approveAll = async () => {
    const count = pendingTotal || pending.length;
    if (!count || approvingAll) return;
    const msg =
      `Approve all ${pendingCountLabel} brand(s) waiting for review?\n\n` +
      "This adds them to the catalog as active. It does not assign them to any publisher.";
    if (!window.confirm(msg)) return;
    setApprovingAll(true);
    const t = toast.loading(`Approving ${pendingCountLabel} brand(s)…`);
    try {
      const { data } = await API.post("/admin/brands/approve-all-pending", {});
      toast.dismiss(t);
      const approved = data.approved_count ?? 0;
      if (approved === 0) {
        toast.info("No brands left to approve — refresh if the count looks stale.");
      } else {
        toast.success(`Approved ${approved} brand(s)`);
      }
      fetchAll();
    } catch (err) {
      toast.dismiss(t);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 404) {
        toast.error("Approve-all endpoint not found — restart the backend and hard-refresh this page.");
      } else {
        toast.error(detail || "Approve all failed");
      }
    } finally {
      setApprovingAll(false);
    }
  };

  const approve = async (campaignId, name) => {
    if (!campaignId) {
      toast.error("Missing campaign_id — refresh the page");
      return;
    }
    try {
      const { data } = await API.post(`/admin/brands/${encodeURIComponent(campaignId)}/approve`);
      if (data?.already) {
        toast.info(`Already approved: ${data.name || name || campaignId}`);
      } else {
        toast.success(`Approved: ${data?.name || name || campaignId}`);
        setPending((rows) => rows.filter((b) => b.campaign_id !== campaignId));
        setPendingTotal((n) => Math.max(0, n - 1));
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    }
  };

  const confirmRemoval = async (b) => {
    const msg =
      `Confirm "${b.name}" is removed from Impact?\n\n` +
      `This will:\n` +
      `  • Mark the brand as removed_from_network\n` +
      `  • Revoke ${b.active_assignments || 0} active publisher assignment(s)\n` +
      `  • Email each affected publisher`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await API.post(
        `/admin/brands/at-risk/${b.campaign_id}/confirm-removal`,
        { notify_pubs: true },
      );
      toast.success(
        `Removed · ${data.revoked_assignments || 0} assignment(s) revoked · ${(data.notified_pubs || []).length} pub(s) notified`
      );
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Removal failed");
    }
  };

  const dismiss = async (b) => {
    if (!window.confirm(`Dismiss the at-risk flag on "${b.name}"? It can be re-flagged on the next sync.`)) return;
    try {
      await API.post(`/admin/brands/at-risk/${b.campaign_id}/dismiss`);
      toast.success("Flag dismissed");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Dismiss failed");
    }
  };

  return (
    <div className="p-8" data-testid="admin-brands-sync">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-400">Brand catalog</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Buildings size={26} weight="bold" /> Impact reconciliation
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Daily diff against Impact&apos;s API. New brands import as <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">active</span> in the catalog
            (no manual approve step). Missing brands flagged at-risk (no auto-revoke). Admin email digest on every actionable change.
          </p>
        </div>
        <Button
          onClick={runNow}
          disabled={running}
          className="bg-[#002FA7] hover:bg-[#002089] text-white"
          data-testid="brands-sync-run-now"
        >
          <ArrowsClockwise size={16} className="mr-1.5" />
          {running ? "Running…" : "Run sync now"}
        </Button>
      </div>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="pending" data-testid="tab-pending">
            🆕 New ({pendingCountLabel})
          </TabsTrigger>
          <TabsTrigger value="atrisk" data-testid="tab-atrisk">
            ⚠️ At risk ({atRisk.length})
          </TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs">
            History ({runs.length})
          </TabsTrigger>
        </TabsList>

        {/* PENDING REVIEW */}
        <TabsContent value="pending">
          {(pendingTotal > 0 || pending.length > 0) && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-600">
                {pendingCountLabel} brand(s) waiting for review.
                {pendingTruncated ? " Showing the first 500 — Approve all clears the full queue." : ""}
                {" "}Approve to onboard into the catalog — no publisher assignment.
              </p>
              <Button
                onClick={approveAll}
                disabled={approvingAll || loading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="brands-sync-approve-all"
              >
                <CheckCircle size={16} className="mr-1.5" />
                {approvingAll ? "Approving…" : `Approve all (${pendingCountLabel})`}
              </Button>
            </div>
          )}
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Network</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Advertiser</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Campaign ID</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Discovered</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((b) => (
                  <tr key={b.campaign_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-900 font-medium">
                      {b.name || <span className="text-slate-400 italic">— (no name)</span>}
                    </td>
                    <td className="px-4 py-3"><NetworkPill network={b.network} /></td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{b.advertiser_name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{b.campaign_id || "—"}</td>
                    <td className="px-4 py-3 text-right text-xs text-slate-500">
                      {b.discovered_at ? new Date(b.discovered_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        onClick={() => approve(b.campaign_id, b.name)}
                        disabled={!b.campaign_id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`approve-${b.campaign_id}`}
                      >
                        <CheckCircle size={14} className="mr-1" /> Approve
                      </Button>
                    </td>
                  </tr>
                ))}
                {pending.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">
                    No brands waiting for review. Impact imports go live automatically; this tab may still list other networks (e.g. FlexOffers).
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* AT RISK */}
        <TabsContent value="atrisk">
          {atRisk.length > 0 && (
            <div className="mb-4 border border-rose-200 bg-rose-50 rounded-md p-4 text-sm text-rose-900">
              <div className="flex items-start gap-2">
                <Warning size={20} weight="fill" className="text-rose-600 mt-0.5" />
                <div>
                  <p className="font-bold">{atRisk.length} brand(s) flagged at-risk</p>
                  <p className="text-xs mt-1 text-rose-800">
                    These brands are no longer returned by Impact's API. Active publisher assignments
                    are still in place. Confirm removal to revoke them + email pubs, or dismiss if it
                    was a transient API blip.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Brand</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Network</th>
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Campaign ID</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Active assignments</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Flagged</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {atRisk.map((b) => (
                  <tr key={b.campaign_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-900">
                      {b.name || <span className="text-slate-400 italic">— (no name)</span>}
                      <div className="text-xs text-slate-500">{b.advertiser_name || ""}</div>
                    </td>
                    <td className="px-4 py-3"><NetworkPill network={b.network} /></td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{b.campaign_id || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                        (b.active_assignments || 0) > 0 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-500"
                      }`}>{b.active_assignments || 0}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-slate-500">
                      {b.at_risk_since ? new Date(b.at_risk_since).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => dismiss(b)}
                          data-testid={`dismiss-${b.campaign_id}`}
                        >
                          Dismiss
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => confirmRemoval(b)}
                          className="bg-rose-600 hover:bg-rose-700 text-white"
                          data-testid={`remove-${b.campaign_id}`}
                        >
                          Confirm removal
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {atRisk.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">
                    Nothing at risk — every brand in our catalog is still on Impact.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* RUNS HISTORY */}
        <TabsContent value="runs">
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Started</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Total on Impact</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-emerald-700 font-bold bg-emerald-50/40">+ New</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-rose-700 font-bold bg-rose-50/40">! Vanished</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-amber-700 font-bold bg-amber-50/40">↻ Recovered</th>
                  <th className="text-right px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Updated</th>
                  <th className="text-center px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
                  <th className="text-center px-4 py-2.5 text-xs uppercase tracking-wider text-slate-500 font-bold">Email</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {r.started_at ? new Date(r.started_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">{r.total_impact ?? "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-xs bg-emerald-50/30 text-emerald-700 font-bold">
                      {r.added_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs bg-rose-50/30 text-rose-700 font-bold">
                      {r.newly_at_risk_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs bg-amber-50/30 text-amber-700">
                      {r.recovered_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs text-slate-600">
                      {r.updated_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        r.status === "completed" ? "bg-emerald-50 text-emerald-700" :
                        r.status === "failed" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"
                      }`}>{r.status || "running"}</span>
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-slate-500">
                      {(r.email_sent_to || []).length > 0 ? (
                        <span className="text-emerald-600 font-bold" title={r.email_sent_to.join(", ")}>✓</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {runs.length === 0 && !loading && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-400">
                    No runs yet. Click "Run sync now" or wait for the next 24h tick.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
